import {
  Box,
  Chip,
  ClickAwayListener,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Cancel, Check, Edit, HelpOutline } from '@mui/icons-material';
import React, { useState } from 'react';
import HelpSlideout from './HelpSlideout';

interface PaperSectionProps {
  children?: React.ReactNode;
  title?: string;
  onTitleChange?: (newTitle: string) => void;
  action?: React.ReactNode;
  icon?: React.ReactNode;
  editable?: boolean;
  variant?: 'default' | 'fullwidth' | 'boxed';
  showEdit?: boolean;
  helpContent?: React.ReactNode;
}

export const PaperSection: React.FC<PaperSectionProps> = ({
  children,
  title = '',
  onTitleChange,
  action,
  icon,
  editable = false,
  variant = 'boxed',
  showEdit = false,
  helpContent,
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(title);
  const [helpOpen, setHelpOpen] = useState(false);

  const handleTitleClick = () => {
    if (editable && onTitleChange) {
      setTitleValue(title);
      setIsEditingTitle(true);
    }
  };

  const handleTitleSave = () => {
    setIsEditingTitle(false);
    if (titleValue !== title && onTitleChange) {
      onTitleChange(titleValue);
    }
  };

  const handleTitleCancel = () => {
    setTitleValue(title);
    setIsEditingTitle(false);
  };

  const handleClickAway = () => {
    if (isEditingTitle) {
      handleTitleCancel();
    }
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleSave();
    } else if (e.key === 'Escape') {
      handleTitleCancel();
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitleValue(e.target.value);
  };

  // Update local state when prop changes
  React.useEffect(() => {
    setTitleValue(title);
  }, [title]);

  return (
    <Box sx={{ position: 'relative', mb: 4 }}>
      {showEdit && (
        <Chip
          label="EDITING NEW VERSION"
          size="small"
          color="success"
          sx={{
            position: 'absolute',
            top: -10,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1,
            fontWeight: 600,
            fontSize: '0.65rem',
            height: 20,
            px: 0.5,
          }}
        />
      )}
      <Paper
        sx={{
          p: 0,
          elevation: 1,
          border: showEdit ? '2px solid' : 'none',
          borderColor: showEdit ? 'success.light' : 'transparent',
        }}
      >
        <Box
          className="paper-section-title"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, mr: 4 }}>
            {icon && <Box sx={{ mr: 2 }}>{icon}</Box>}
            {title && (
              <Box
                sx={{
                  position: 'relative',
                  flex: 1,
                  '&:hover .edit-icon': {
                    opacity: editable && onTitleChange ? 1 : 0,
                  },
                }}
              >
                {editable && isEditingTitle ? (
                  <ClickAwayListener onClickAway={handleClickAway}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        value={titleValue}
                        onChange={handleTitleChange}
                        onKeyDown={handleTitleKeyDown}
                        autoFocus
                        variant="outlined"
                        size="small"
                        sx={{
                          flex: 1,
                          minWidth: 300,
                          '& .MuiInputBase-root': {
                            fontSize: '1rem !important',
                          },
                          '& .MuiInputBase-input': {
                            fontSize: '1rem !important',
                            fontWeight: 400,
                            fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                            py: 0.5,
                          },
                        }}
                      />
                      <IconButton size="small" onClick={handleTitleCancel} color="default">
                        <Tooltip title="Cancel">
                          <Cancel />
                        </Tooltip>
                      </IconButton>
                      <IconButton size="small" onClick={handleTitleSave} color="primary">
                        <Tooltip title="Save">
                          <Check />
                        </Tooltip>
                      </IconButton>
                    </Box>
                  </ClickAwayListener>
                ) : (
                  <>
                    <Box
                      onClick={handleTitleClick}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        cursor: editable && onTitleChange ? 'pointer' : 'default',
                        '&:hover': {
                          bgcolor: editable && onTitleChange ? 'action.hover' : 'transparent',
                        },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 400,
                        }}
                      >
                        {title}
                      </Typography>
                    </Box>
                    {editable && onTitleChange && (
                      <IconButton
                        className="edit-icon"
                        size="small"
                        onClick={handleTitleClick}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          bgcolor: 'background.paper',
                          boxShadow: 1,
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {helpContent && (
              <Tooltip title="Help">
                <IconButton onClick={() => setHelpOpen(true)}>
                  <HelpOutline />
                </IconButton>
              </Tooltip>
            )}
            {action}
          </Stack>
        </Box>
        <Box
          sx={{
            p: variant === 'fullwidth' ? 0 : { xs: 2, sm: 4, md: 6 },
            pt: variant === 'fullwidth' ? 0 : { xs: 2, sm: 3, md: 4 },
            maxWidth: variant === 'boxed' ? 700 : 'none',
            mx: variant === 'boxed' ? 'auto' : 0,
          }}
        >
          {children}
        </Box>
      </Paper>
      {helpContent && (
        <HelpSlideout open={helpOpen} onClose={() => setHelpOpen(false)}>
          {helpContent}
        </HelpSlideout>
      )}
    </Box>
  );
};

export default PaperSection;
